// Where emoji reactions land in the feed. A reaction is stored as its own row
// that names the message it belongs on; the feed hides that row and draws the
// emoji on the target instead. okthx reacts to the user's typed messages, and
// its row names the target in a `userResponse` artifact. The user reacts to
// okthx's replies with a `autoReaction` row, which names the target itself in
// `replyToMessageId`. The target's type says whose emoji a badge is.
// Pure (types only) so it lives here in shared/client and both apps read the
// same placement.
import type { GetArtifactResp } from "@/types/artifacts.ts";
import type { GetMessageResp } from "@/types/messages.ts";
import type { RootItem } from "./eventTree.ts";

export type FeedReactions = {
  /** Emoji to draw on each message, in the order they were left. */
  reactionsByMessageId: ReadonlyMap<number, readonly string[]>;
  /** Root-item indices of the reaction rows the feed draws elsewhere. */
  reactionIndices: ReadonlySet<number>;
};

/** Shared empty value, so an idle feed keeps a stable identity across renders. */
export const NO_FEED_REACTIONS: FeedReactions = {
  reactionsByMessageId: new Map(),
  reactionIndices: new Set(),
};

export type Reaction = { emoji: string; targetMessageId: number };

export const isReactionArtifact = (artifact: GetArtifactResp): boolean =>
  !artifact.isHeavy && artifact.artifactType === "userResponse" && artifact.kind === "reaction";

/** The reaction a message carries, if it is a reaction row at all. */
export function reactionOf(message: GetMessageResp): Reaction | undefined {
  if (message.type === "autoReaction") {
    if (message.replyToMessageId === undefined || !message.content) return undefined;
    return { emoji: message.content, targetMessageId: message.replyToMessageId };
  }
  for (const artifact of message.artifacts ?? []) {
    if (artifact.isHeavy || artifact.artifactType !== "userResponse") continue;
    if (artifact.kind !== "reaction" || artifact.targetMessageId === undefined) continue;
    if (!artifact.response) continue;
    return { emoji: artifact.response, targetMessageId: artifact.targetMessageId };
  }
  return undefined;
}

const BADGE_HOST_TYPES: ReadonlySet<string> = new Set(["userRequest", "agentResponse"]);

/**
 * Pair every reaction row with the message it belongs on.
 *
 * Only the user's typed messages (`userRequest`, for okthx's reactions) and
 * okthx's replies (`agentResponse`, for the user's) draw a badge, so only a
 * reaction on one of those is taken out of the feed. A row of okthx's whose
 * target is anything else, or is not in the feed at all (paged out, or
 * deleted), is left where it is and renders as the short reply it also is
 * rather than vanishing: a badge with nothing to sit on is worse than a bubble.
 *
 * The user's own `autoReaction` is always taken out. It never reads as a
 * message, even when the reply it is on is not in the feed.
 */
export function selectReactions(rootItems: RootItem[]): FeedReactions {
  const badgeHosts = new Set<number>();
  for (const item of rootItems) {
    if (item.kind === "message" && BADGE_HOST_TYPES.has(item.event.message.type)) {
      badgeHosts.add(item.event.message.id);
    }
  }

  const reactionsByMessageId = new Map<number, string[]>();
  const reactionIndices = new Set<number>();
  rootItems.forEach((item, index) => {
    if (item.kind !== "message") return;
    const message = item.event.message;
    if (message.status === "aborted") return;
    const reaction = reactionOf(message);
    if (!reaction || !badgeHosts.has(reaction.targetMessageId)) {
      if (message.type === "autoReaction") reactionIndices.add(index);
      return;
    }
    // okthx's reactions stack. The user's replace each other: a change is
    // filed as a new row, and only the newest one, last in feed order, counts.
    const emojis =
      message.type === "autoReaction" ? [] : (reactionsByMessageId.get(reaction.targetMessageId) ?? []);
    emojis.push(reaction.emoji);
    reactionsByMessageId.set(reaction.targetMessageId, emojis);
    reactionIndices.add(index);
  });

  if (reactionIndices.size === 0) return NO_FEED_REACTIONS;
  return { reactionsByMessageId, reactionIndices };
}
